import { HeaderActionsService } from '@adminprodigydesign/axis';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HtmlEntityDecoderService } from '../../../shared/services/html-entity-decoder.service';

type DayHours = {
  day: string; // "Mon", "Tuesday", etc.
  isClosed?: boolean;
  open?: Date | string; // if single interval
  close?: Date | string;
  intervals?: { open: Date | string; close: Date | string }[];
};

@Component({
  selector: 'app-restaurant-header',
  standalone: false,
  templateUrl: './restaurant-header.component.html',
  styleUrl: './restaurant-header.component.scss',
})
export class RestaurantHeaderComponent {
  private _restaurantName = '';
  private _subtitle = '';

  @Input()
  set restaurantName(value: string | null | undefined) {
    this._restaurantName = this.htmlDecoder.decode(value || '');
  }

  get restaurantName(): string {
    return this._restaurantName;
  }

  @Input()
  set subtitle(value: string | null | undefined) {
    this._subtitle = this.htmlDecoder.decode(value || '');
  }

  get subtitle(): string {
    return this._subtitle;
  }
  @Input() deliveryTime: string = '0';
  @Input() distance!: number;

  @Input() isClosed: boolean = false;
  @Input() minOrder!: number;
  @Input() deliveryFee!: number;
  @Input() rating!: number;
  @Input() reviewCount!: number;
  @Input() hygieneRating?: string;
  @Input() hygieneUrl = 'https://ratings.food.gov.uk/';
  @Input() allergensNote = '';
  @Input() extraNotes = '';
  @Input() restaurantAddress = '';
  @Input() orderType: 'delivery' | 'collection' = 'delivery';
  @Output() orderTypeChange = new EventEmitter<'delivery' | 'collection'>();

  @Input() openingTime?: string;
  @Input() closingTime!: string;
  @Input() openingTimeRaw?: string;
  @Input() closingTimeRaw?: string;

  @Input() openingHours: DayHours[] = [];
  @Input() deliveryHours: DayHours[] = [];
  @Input() fees?: {
    minOrder: number;
    deliveryFee: number;
    serviceFee?: number;
  };
  @Input() deliveryToggleNote = '';

  // Filled from API (see service below)
  fsaRatingText?: string; // e.g. "5 - Very Good"
  fsaLastUpdated?: string; // ISO string
  hygieneError?: boolean;

  constructor(
    private headerActionService: HeaderActionsService,
    private htmlDecoder: HtmlEntityDecoderService,
  ) {}

  openInfoModal(): void {
    this.headerActionService.triggerInfoClick();
  }

  get googleMapsUrl(): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      this.restaurantAddress || '',
    )}`;
  }

  formatTime(value?: string | Date): string {
    if (!value) return '—';
    if (value instanceof Date) {
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }).format(value);
    }

    const parts = value.split(':').map(Number);
    if (parts.length < 2) return value;

    const d = new Date();
    d.setHours(parts[0] || 0, parts[1] || 0, 0, 0);

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  }

  get hasInfo(): boolean {
    return !!(
      this.allergensNote ||
      this.hygieneRating ||
      this.restaurantAddress ||
      this.extraNotes
    );
  }

  changeOrderType(type: 'delivery' | 'collection') {
    this.orderTypeChange.emit(type);
  }

  /**
   * True if the shop should be shown as closed right now:
   *  - closed by schedule (API: isClosed)
   *  - before openingTimeRaw
   *  - at or after closingTimeRaw
   */
  get computedIsClosed(): boolean {
    return this.isClosed;
  }
}
